import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import TextInput from "../../library/TextInput";
import SelectDropdown from "../../library/SelectDropdown";
import ResizableTextarea from "../../library/ResizableTextarea";
import CommonButton from "../../common/Button/Button";
import PositiveButton from "../../library/PositiveButton";
import ButtonGroup from "../../library/ButtonGroup";
import {
    CreateFirstAidBox,
    UpdateFirstAidBox,
    UpdateFirstAidBoxAssignment
} from "../../../services/FirstAidBoxService";
import {
    invalidateFirstAidBoxQueries,
    useFirstAidBox,
    useFirstAidBoxListFilters,
    useFirstAiderCandidates,
    useOhsAccess
} from "../../../query/hooks/QueryHooksFirstAidBox";
import { useCampusBuildingsList } from "../../../query/hooks/QueryHooksCampusBuilding";
import { mapBuildingsToSelect } from "../../../utils/CampusBuildingsUtil";
import { OHS_BASE_PATH } from "./ohsConstants";
import { FormGrid, SectionCard, SectionHeader, SectionTitle, MutedText } from "./Ohs.styles";
import { getOhsErrorMessage } from "./ohsUtils";
import ConditionBadges from "./ConditionBadges";
import BoxCurrentStock from "./BoxCurrentStock";

const EMPTY_FORM = {
    buildingId: "",
    areaLocation: "",
    boxType: "",
    serialNumber: "",
    verificationNote: "",
    assignedFirstAiderId: "",
    alternateFirstAiderId: "",
};

/**
 * Create/edit a box - details and first aider assignment, saved together by the page's single
 * Save button. Deleting is done from the Boxes table, not here.
 * @param {Object} props
 * @param {string} [props.boxId] Box being edited; omitted when creating one.
 */
function BoxDetails({ boxId }) {
    const history = useHistory();
    const isCreating = !boxId;
    const [formValues, setFormValues] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const { data: ohsAccess } = useOhsAccess();
    // Adding, editing and assigning boxes are all manager functions.
    const canManage = ohsAccess.canManage;

    const { data: box, isLoading: isBoxLoading } = useFirstAidBox(boxId);
    const { data: formOptions = {} } = useFirstAidBoxListFilters();
    const { data: campusBuildings, isLoading: isBuildingsLoading } = useCampusBuildingsList();
    const { data: firstAiderCandidates } = useFirstAiderCandidates(canManage);

    useEffect(() => {
        if (!box) {
            return;
        }
        setFormValues({
            buildingId: box.building_id,
            areaLocation: box.area_location || "",
            boxType: box.box_type || "",
            serialNumber: box.serial_number || "",
            verificationNote: box.verification_note || "",
            assignedFirstAiderId: box.assigned_first_aider_id || "",
            alternateFirstAiderId: box.alternate_first_aider_id || "",
        });
    }, [box]);

    const buildingOptions = useMemo(() => mapBuildingsToSelect(campusBuildings), [campusBuildings]);

    const setFormValue = (fieldName, value) => {
        setFormValues((currentValues) => ({ ...currentValues, [fieldName]: value }));
        setFormErrors((currentErrors) => ({ ...currentErrors, [fieldName]: undefined }));
    };

    const handleSave = async () => {
        const requiredFieldErrors = {
            buildingId: formValues.buildingId ? undefined : "Campus building is required",
            areaLocation: formValues.areaLocation.trim() ? undefined : "Area is required",
            boxType: formValues.boxType ? undefined : "Box type is required",
        };
        if (Object.values(requiredFieldErrors).some(Boolean)) {
            setFormErrors(requiredFieldErrors);
            return;
        }

        const boxDetails = {
            buildingId: formValues.buildingId,
            areaLocation: formValues.areaLocation.trim(),
            boxType: formValues.boxType,
            serialNumber: formValues.serialNumber.trim() || null,
            verificationNote: formValues.verificationNote.trim() || null,
        };
        const assignment = {
            assignedFirstAiderId: formValues.assignedFirstAiderId || null,
            alternateFirstAiderId: formValues.alternateFirstAiderId || null,
        };

        setIsSaving(true);
        try {
            if (isCreating) {
                const { data } = await CreateFirstAidBox({ ...boxDetails, ...assignment });
                await invalidateFirstAidBoxQueries();
                Swal.fire("Box added", `Box ${data.data.box_code} was created.`, "success");
                history.push(`${OHS_BASE_PATH}/boxes/open/${data.data.id}`);
                return;
            }

            await UpdateFirstAidBox({ id: boxId, ...boxDetails });
            const isAssignmentChanged = Number(assignment.assignedFirstAiderId || 0) !== Number(box.assigned_first_aider_id || 0)
                || Number(assignment.alternateFirstAiderId || 0) !== Number(box.alternate_first_aider_id || 0);
            if (isAssignmentChanged) {
                await UpdateFirstAidBoxAssignment({ id: boxId, ...assignment });
            }
            await invalidateFirstAidBoxQueries();
            Swal.fire("Saved", "The box was updated.", "success");
        } catch (error) {
            console.log("BoxDetails.js->handleSave->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The box could not be saved."), "error");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isCreating && isBoxLoading) {
        return <SkeletonTicketList />;
    }
    if (!isCreating && !box) {
        return <MutedText>This box could not be found.</MutedText>;
    }

    const firstAiderOptions = firstAiderCandidates.map((candidate) => ({
        value: candidate.value,
        label: candidate.email ? `${candidate.label} (${candidate.email})` : candidate.label,
    }));

    return (
        <>
            <SectionCard>
                <SectionHeader>
                    <SectionTitle>
                        {isCreating ? "Add Box" : `Box ${box.box_code}`}
                    </SectionTitle>
                    {!isCreating && <ConditionBadges statuses={box.statuses || [box.last_check_condition]} />}
                </SectionHeader>

                <FormGrid>
                    <SelectDropdown
                        id="ohsCampusBuilding"
                        label="Campus Building"
                        required
                        options={buildingOptions}
                        value={formValues.buildingId}
                        onChange={(event) => setFormValue("buildingId", event.target.value)}
                        errorText={formErrors.buildingId}
                        helperText="The box's campus is taken from its building."
                        disabled={!canManage || isBuildingsLoading}
                        placeholder={isBuildingsLoading ? "Loading buildings..." : "Select building"}
                    />
                    <TextInput
                        id="ohsArea"
                        label="Area / Location"
                        required
                        placeholder="e.g. Reception, Vehicle bay"
                        maxLength={150}
                        value={formValues.areaLocation}
                        onChange={(event) => setFormValue("areaLocation", event.target.value)}
                        errorText={formErrors.areaLocation}
                        error={Boolean(formErrors.areaLocation)}
                        disabled={!canManage}
                    />
                    <SelectDropdown
                        id="ohsBoxType"
                        label="Box Type"
                        required
                        options={formOptions.BoxTypeOptions || []}
                        value={formValues.boxType}
                        onChange={(event) => setFormValue("boxType", event.target.value)}
                        errorText={formErrors.boxType}
                        disabled={!canManage}
                        isSearchable={false}
                    />
                    {/* Managers only - first aiders must read the tag off the box, so they never see the one on record. */}
                    {canManage && (
                        <TextInput
                            id="ohsSerialNumber"
                            label="Current Tag Serial"
                            placeholder="Serial on the box's tamper tag"
                            maxLength={60}
                            value={formValues.serialNumber}
                            onChange={(event) => setFormValue("serialNumber", event.target.value)}
                            helperText="Updated automatically whenever a check or QR usage fits a new tag."
                        />
                    )}
                    {!isCreating && (
                        <TextInput
                            id="ohsBoxCode"
                            label="Box Code"
                            value={box.box_code}
                            helperText="Generated from the campus and building when the box was added."
                            disabled
                        />
                    )}
                    <div className="ohs-field--full">
                        <ResizableTextarea
                            id="ohsVerificationNote"
                            label="Verification Note"
                            rows={3}
                            placeholder="Anything an OHS Representative should know about this box"
                            value={formValues.verificationNote}
                            onChange={(event) => setFormValue("verificationNote", event.target.value)}
                            disabled={!canManage}
                        />
                    </div>
                </FormGrid>
                {isCreating && (
                    <MutedText>
                        Buildings come from <Link to="/administration/campusManagement/campusList">Campus Management</Link>.
                    </MutedText>
                )}
            </SectionCard>

            <SectionCard>
                <SectionHeader>
                    <SectionTitle>First Aiders</SectionTitle>
                </SectionHeader>
                {canManage ? (
                    <FormGrid>
                        <SelectDropdown
                            id="ohsAssignedFirstAider"
                            label="Assigned First Aider"
                            options={firstAiderOptions}
                            value={formValues.assignedFirstAiderId}
                            onChange={(event) => setFormValue("assignedFirstAiderId", event.target.value)}
                            helperText="Only people on the First Aiders list can be assigned."
                            isClearable
                        />
                        <SelectDropdown
                            id="ohsAlternateFirstAider"
                            label="Alternate First Aider"
                            options={firstAiderOptions}
                            value={formValues.alternateFirstAiderId}
                            onChange={(event) => setFormValue("alternateFirstAiderId", event.target.value)}
                            isClearable
                        />
                    </FormGrid>
                ) : (
                    <FormGrid>
                        <TextInput id="ohsAssignedReadOnly" label="Assigned First Aider" value={box?.assigned_name || "Unassigned"} disabled />
                        <TextInput id="ohsAlternateReadOnly" label="Alternate First Aider" value={box?.alternate_name || "None"} disabled />
                    </FormGrid>
                )}
            </SectionCard>

            {!isCreating && <BoxCurrentStock currentStock={box.currentStock} />}

            {canManage && (
                <ButtonGroup>
                    <PositiveButton onClick={handleSave} disabled={isSaving} />
                    <CommonButton type="button" variant="danger" onClick={() => history.push(`${OHS_BASE_PATH}/boxes`)} iconLeft={<i className="fal fa-times"></i>}>
                        Cancel
                    </CommonButton>
                </ButtonGroup>
            )}
        </>
    );
}

export default BoxDetails;
